import { registerPlugin } from '@capacitor/core';
import { isCapacitorNative } from 'environments/environment-helpers';

export enum Feedback {
  KeyboardTap = 'keyboardTap',
  PickerTick = 'pickerTick',
  ImpactLight = 'impactLight',
  ImpactMedium = 'impactMedium',
  ImpactHeavy = 'impactHeavy',
  Selection = 'selection',
  Beep = 'beep',
  Buzz = 'buzz',
}

interface HapticsPluginInterface {
  feedback(options: { type: Feedback }): Promise<void>;
}

const HapticsPlugin = registerPlugin<HapticsPluginInterface>('HapticsPlugin');

// web audio state (lazy-loaded on first sound request)
let audioContext: AudioContext | null = null;
let clickSoundBuffer: AudioBuffer | null = null;
let clickSoundLoading = false;

// lazy-load AudioContext and click sound buffer for web
function ensureWebAudio() {
  if (audioContext || clickSoundLoading || isCapacitorNative()) return;
  clickSoundLoading = true;
  try {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    void fetch('/assets/sounds/button_tap.wav')
      .then(r => r.arrayBuffer())
      .then(buf => audioContext!.decodeAudioData(buf))
      .then(decoded => { clickSoundBuffer = decoded; })
      .catch(() => {
        // if fetch/decode fails, allow retry on next interaction
        clickSoundLoading = false;
        console.log('Failed to load click sound');
      });
  } catch (e) {
    clickSoundLoading = false;
    console.log('AudioContext not supported');
  }
}

// play the pre-loaded click sound at given volume
function playWebSound(volume: number) {
  if (!clickSoundBuffer || !audioContext) return;
  try {
    const source = audioContext.createBufferSource();
    const gainNode = audioContext.createGain();
    source.buffer = clickSoundBuffer;
    source.connect(gainNode);
    gainNode.connect(audioContext.destination);
    gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
    source.start();
  } catch (e) {
    console.log('Failed to play click sound');
  }
}

// generate a short 2500Hz triangle wave beep using the shared AudioContext
function playWebBeep() {
  ensureWebAudio();
  if (!audioContext) return;
  try {
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    oscillator.connect(gain);
    oscillator.frequency.value = 2500;
    oscillator.type = 'triangle';
    gain.connect(audioContext.destination);
    gain.gain.value = 0.3;
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.05);
  } catch (e) {
    console.log('Failed to play beep');
  }
}

// trigger device vibration on web
function webVibrate(ms: number) {
  if ('vibrate' in navigator) {
    navigator.vibrate(ms);
  }
}

export function triggerFeedback(type: Feedback) {
  if (isCapacitorNative()) {
    void HapticsPlugin.feedback({ type });
    return;
  }

  // web fallback
  switch (type) {
    case Feedback.KeyboardTap:
      ensureWebAudio();
      playWebSound(0.3);
      webVibrate(10);
      break;
    case Feedback.PickerTick:
      ensureWebAudio();
      playWebSound(0.1);
      webVibrate(5);
      break;
    case Feedback.Beep:
      playWebBeep();
      break;
    case Feedback.ImpactLight:
      webVibrate(10);
      break;
    case Feedback.ImpactMedium:
    case Feedback.ImpactHeavy:
      webVibrate(20);
      break;
    case Feedback.Selection:
      webVibrate(5);
      break;
    case Feedback.Buzz:
      webVibrate(400);
      break;
  }
}
